import { useState } from 'react';
import { Alert, Badge, Button, Field, Modal, Stack, TextArea } from '@integra/ui';
import { useGenerateImage } from '../api/hooks.js';

/**
 * Generate with AI (creative source, Phase 2). The GENERATE entry point: a brief
 * becomes a brand-new creative rendered by an image-generation API and landed as
 * a high-resolution draft Asset — the same destination "Import from Canva"
 * reaches, so it flows through repurpose + publish identically.
 *
 * All data access is through the hooks layer (§21.4) — this component performs no
 * HTTP of its own. Nothing here publishes: the generated image is a draft for
 * review and repurposing, and is disclosed as AI-authored server-side.
 */
export function GenerateImageModal({
  workspaceId,
  open,
  onClose,
}: {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [generatedTitle, setGeneratedTitle] = useState<string | null>(null);
  const generate = useGenerateImage(workspaceId);

  const run = (): void => {
    setGeneratedTitle(null);
    generate.mutate(
      { prompt },
      {
        onSuccess: (res) => {
          setGeneratedTitle(res.data.title);
          setPrompt('');
        },
      },
    );
  };

  const close = (): void => {
    setGeneratedTitle(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Generate with AI"
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {generatedTitle !== null ? 'Done' : 'Cancel'}
          </Button>
          <Button onClick={run} loading={generate.isPending} disabled={prompt.trim().length === 0}>
            Generate
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <p className="text-sm text-ink-muted">
          Describe the image you want. It lands as a high-resolution draft asset you can repurpose
          per channel and publish — nothing is posted here, and it&apos;s disclosed as AI-authored.
        </p>

        <Field label="Prompt" htmlFor="ai-prompt">
          <TextArea
            id="ai-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="A warm autumn flat-lay of a gift hamper on a linen background, soft daylight"
          />
        </Field>

        {generate.error instanceof Error && (
          <Alert tone="error" title="Generation failed">
            {generate.error.message}
          </Alert>
        )}

        {generatedTitle !== null && (
          <Alert tone="success" title="Image generated">
            <Stack gap="sm">
              <span>
                “{generatedTitle}” landed as a draft image asset. Generate another, or close to see
                it in your assets.
              </span>
              <Badge tone="success" dot>
                Draft created
              </Badge>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Modal>
  );
}
